import { createSelector } from '@reduxjs/toolkit';
import { Asset } from '../../api';
import { getStore, RootSelector } from '../../reducers';
import {
  getData as getFavoriteData,
  getHasNextPage as getFavoriteHasNextPage,
  getLoading as getFavoriteLoading,
  getScrollHash as getFavoriteScrollHash,
} from '../favorite/selectors';
import {
  getData as getPackData,
  getHasNextPage as getPackHasNextPage,
  getLoading as getPackLoading,
  getScrollHash as getPackScrollHash,
} from '../pack/selectors';
import { Page } from '../router/model';
import { getPage } from '../router/selectors';
import {
  getData as getSharingData,
  getHasNextPage as getSharingHasNextPage,
  getLoading as getSharingLoading,
  getScrollHash as getSharingScrollHash,
} from '../sharing/selectors';
import {
  getData as getViewerData,
  getHasNextPage as getViewerHasNextPage,
  getLoading as getViewerLoading,
  getScrollHash as getViewerScrollHash,
} from '../viewer/selectors';
import { ViewMode } from '../viewMode/model';
import { getViewMode } from '../viewMode/selectors';
import {
  EMPTY_TOTAL,
  FinderSelected,
  FinderShape,
  Loading,
  Mode,
  MODES,
  SCROLLBAR_WIDTH,
} from './model';

export const getFinder: RootSelector<FinderShape> = createSelector(
  getStore,
  (state) => state.finder,
);

export const getMode: RootSelector<Mode | null> = createSelector(getPage, (page) => {
  function includes<T extends U, U>(coll: ReadonlyArray<T>, el: U): el is T {
    return coll.includes(el as T);
  }
  return includes(MODES, page) ? page : null;
});

type GetDataResult =
  | ReturnType<typeof getViewerData>
  | ReturnType<typeof getPackData>
  | ReturnType<typeof getFavoriteData>
  | ReturnType<typeof getSharingData>;
export const getData: RootSelector<GetDataResult> = createSelector(
  [getMode, getViewerData, getPackData, getFavoriteData, getSharingData],
  (mode, viewerData, packData, favoriteData, sharingData) => {
    if (!mode) {
      return { assets: [], total: EMPTY_TOTAL };
    }
    const source: Record<Mode, GetDataResult> = {
      [Page.viewer]: viewerData,
      [Page.pack]: packData,
      [Page.favorite]: favoriteData,
      [Page.sharing]: sharingData,
    };
    return source[mode];
  },
);

export const getAssetsById: RootSelector<Partial<Record<Asset['id'], Asset>>> = createSelector(
  getData,
  (data) => {
    const result: Partial<Record<Asset['id'], Asset>> = {};
    data.assets.forEach((asset) => {
      result[asset.id] = asset;
    });
    return result;
  },
);

export const getSelected: RootSelector<FinderSelected> = createSelector(
  getFinder,
  (finder) => finder.selected,
);

export const makeGetIsAssetSelected = (assetId: Asset['id']) =>
  createSelector(getSelected, (selected) => selected.ids.includes(assetId));

export const makeGetAssetById = (assetId: Asset['id']) =>
  createSelector(getAssetsById, (assetsById) => assetsById[assetId]);

export const getAssetDimensions: RootSelector<{ assetWidth: number; assetHeight: number }> =
  createSelector(getViewMode, (viewMode) => ({
    assetWidth: viewMode === ViewMode.assets ? 138 : 260,
    assetHeight: viewMode === ViewMode.assets ? 172 : 200,
  }));

export const getDetailsAssets: RootSelector<Asset[]> = createSelector(
  [getSelected, getAssetsById],
  (selected, assetsById) =>
    selected.ids.map((id) => assetsById[id]).filter((asset): asset is Asset => Boolean(asset)),
);

export const getDetailsAssetsSize: RootSelector<number> = createSelector(
  getDetailsAssets,
  (detailsAssets) => detailsAssets.reduce((sum, asset) => sum + asset.size, 0),
);

export const getLoading: RootSelector<Loading> = createSelector(
  [getMode, getViewerLoading, getPackLoading, getFavoriteLoading, getSharingLoading],
  (mode, viewerLoading, packLoading, favoriteLoading, sharingLoading) => {
    if (!mode) {
      return Loading.none;
    }
    const source: Record<Mode, Loading> = {
      [Page.viewer]: viewerLoading,
      [Page.pack]: packLoading,
      [Page.favorite]: favoriteLoading,
      [Page.sharing]: sharingLoading,
    };
    return source[mode];
  },
);

export const getHasNextPage: RootSelector<boolean> = createSelector(
  [
    getMode,
    getViewerHasNextPage,
    getPackHasNextPage,
    getFavoriteHasNextPage,
    getSharingHasNextPage,
  ],
  (mode, viewerHasNextPage, packHasNextPage, favoriteHasNextPage, sharingHasNextPage) => {
    if (!mode) {
      return true;
    }
    const source: Record<Mode, boolean> = {
      [Page.viewer]: viewerHasNextPage,
      [Page.pack]: packHasNextPage,
      [Page.favorite]: favoriteHasNextPage,
      [Page.sharing]: sharingHasNextPage,
    };
    return source[mode];
  },
);

export const getScrollHash: RootSelector<string> = createSelector(
  [getMode, getViewerScrollHash, getPackScrollHash, getFavoriteScrollHash, getSharingScrollHash],
  (mode, viewerScrollHash, packScrollHash, favoriteScrollHash, sharingScrollHash) => {
    if (!mode) {
      return '';
    }
    const source: Record<Mode, string> = {
      [Page.viewer]: viewerScrollHash,
      [Page.pack]: packScrollHash,
      [Page.favorite]: favoriteScrollHash,
      [Page.sharing]: sharingScrollHash,
    };
    return source[mode];
  },
);

export const getDetails: RootSelector<FinderShape['details']> = createSelector(
  getFinder,
  (finder) => finder.details,
);

export const getDetailsLoading: RootSelector<boolean> = createSelector(
  getFinder,
  (finder) => finder.detailsLoading,
);

export const getDetailsVisible: RootSelector<boolean> = createSelector(
  getFinder,
  (finder) => finder.detailsVisible,
);

export const getContainerWidth: RootSelector<number> = createSelector(
  getFinder,
  (finder) => finder.containerWidth,
);

export type AssetsComposition = {
  containerWidth: number;
  assetWidth: number;
  assetHeight: number;
  gapHorizontal: number;
  gapVertical: number;
  assetsPerRow: number;
  rows: Asset[][];
};
export const getAssetsComposition: RootSelector<AssetsComposition> = createSelector(
  [getData, getContainerWidth, getViewMode],
  (data, containerWidth, viewMode) => {
    const composition: AssetsComposition = {
      containerWidth,
      assetWidth: viewMode === ViewMode.assets ? 138 : 260,
      assetHeight: viewMode === ViewMode.assets ? 172 : 200,
      gapHorizontal: 16,
      gapVertical: 16,
      assetsPerRow: 0,
      rows: [],
    };

    composition.assetsPerRow = Math.floor(
      Math.max(composition.containerWidth - SCROLLBAR_WIDTH, 0) /
        (composition.assetWidth + composition.gapHorizontal),
    );

    if (composition.assetsPerRow === 0) {
      return composition;
    }

    const cells = [...data.assets];
    while (cells.length > 0) {
      const row = cells.splice(0, composition.assetsPerRow);
      composition.rows.push(row);
    }

    return composition;
  },
);

export const getNavigation: RootSelector<{
  prev: Asset | null;
  next: Asset | null;
  curr: Asset | null;
}> = createSelector([getData, getSelected, getDetails], (data, selected, details) => {
  // Находим выбранный позже всех ассет
  const index = data.assets.findIndex((x) => x.id === selected.lastId);
  if (index === -1) {
    // w t f
    return { prev: null, next: null, curr: details.asset ?? null };
  }
  const nextIndex = index + 1;
  const prevIndex = index - 1;
  const next = nextIndex >= 0 && nextIndex < data.assets.length ? data.assets[nextIndex] : null;
  const prev = prevIndex >= 0 && prevIndex < data.assets.length ? data.assets[prevIndex] : null;
  const current = index >= 0 && index < data.assets.length ? data.assets[index] : null;

  let curr = current;
  if (details.asset !== undefined && curr !== null && details.asset.id === curr.id) {
    curr = details.asset;
  }
  return { next, prev, curr };
});

export const getSlotHeight = createSelector(getFinder, (finder) => finder.slotHeight);
export const getSlotDragging = createSelector(getFinder, (finder) => finder.slotDragging);

export const getIsOriginalView = createSelector(getFinder, (finder) => finder.isOriginalView);
